import { FC } from "react";
import {
  ScreenSizeProvider,
  theme as baseTheme,
  createTheme,
} from "flicket-ui";
import { ThemeProvider } from "styled-components";
import {
  BaseProvider,
  UserProvider,
  ReservationProvider,
  TicketNamingProvider,
  ShopifyProvider,
} from "~context";
import { CurrentOrganizationQuery } from "~graphql/sdk";
import { ReCaptchaProvider } from "~context/recaptchaContext";
import ErrorBoundary from "~components/errorBoundary";
import { Router } from "next/router";
import { AnalyticsConsentProvider } from "~features/cookies-consent-providers/AnalyticsConsentProvider";
import { MarketingConsentProvider } from "~features/cookies-consent-providers/MarketingConsentProvider";
import { CookieConsentProvider } from "~context/cookieConsentContext";

export const appTheme = {
  ...baseTheme,
  shadows: {
    ...baseTheme.shadows,
    card: "0px 0px 8px 1px rgba(0, 0, 0, 0.15)",
  },
  fonts: {
    ...baseTheme.fonts,
    body: "Figtree, sans-serif",
    heading: "Figtree, sans-serif",
  },
};

export const Providers: FC<{
  organization: CurrentOrganizationQuery["currentOrganization"];
  router?: Router;
}> = ({ children, organization }) => {
  const organizationColors = {
    ...(organization?.branding?.P100 && { P100: organization.branding.P100 }),
    ...(organization?.branding?.P200 && { P200: organization.branding.P200 }),
    ...(organization?.branding?.P300 && { P300: organization.branding.P300 }),
    ...(organization?.branding?.S100 && { S100: organization.branding.S100 }),
    ...(organization?.branding?.S200 && { S200: organization.branding.S200 }),
    ...(organization?.branding?.S300 && { S300: organization.branding.S300 }),
  };

  const brandedTheme = {
    ...appTheme,
    colors: {
      ...appTheme.colors,
      ...organizationColors,
    },
  };

  const recaptchaKey = process.env.RECAPTCHA_SITE_KEY;

  return (
    <ThemeProvider theme={createTheme(brandedTheme)}>
      <BaseProvider organization={organization}>
        <UserProvider>
          {/* ErrorBoundary currently relies on the theme, the organization and 
              the user for the components it renders on the page. Until that changes
              it needs to be decalred after. */}
          <ErrorBoundary organization={organization}>
            <CookieConsentProvider>
              <AnalyticsConsentProvider>
                <MarketingConsentProvider>
                  <ScreenSizeProvider>
                    <ReservationProvider>
                      <ShopifyProvider>
                        <TicketNamingProvider>
                          <ReCaptchaProvider reCaptchaKey={recaptchaKey}>
                            {children}
                          </ReCaptchaProvider>
                        </TicketNamingProvider>
                      </ShopifyProvider>
                    </ReservationProvider>
                  </ScreenSizeProvider>
                </MarketingConsentProvider>
              </AnalyticsConsentProvider>
            </CookieConsentProvider>
          </ErrorBoundary>
        </UserProvider>
      </BaseProvider>
    </ThemeProvider>
  );
};

export const NoOrgProviders: FC = ({ children }) => {
  return (
    <ThemeProvider theme={createTheme(appTheme)}>
      <BaseProvider
        organization={{} as CurrentOrganizationQuery["currentOrganization"]}
      >
        <ScreenSizeProvider>
          <UserProvider>{children}</UserProvider>
        </ScreenSizeProvider>
      </BaseProvider>
    </ThemeProvider>
  );
};
